import React, { useState, useEffect, useCallback } from "react";
import { END_URL, IMG_URL } from "../../config";
import Dashboard from "../Dashboard/Dashboard";
import "./CommunityPosts.css";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import DOMPurify from "dompurify";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";


function CommunityPosts({ globaltest }) {
    const [showModal, setShowModal] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [posts, setPosts] = useState([]);
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [editingPostId, setEditingPostId] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [loadingPageNumber, setLoadingPageNumber] = useState(null);
    const quillModules = {
        toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
        ],
    };

    const quillFormats = ["bold", "italic", "underline", "list", "bullet", "link",];

    const fetchPosts = useCallback(async (page = 1, showMainLoading = true) => {
        try {
            if (showMainLoading) {
                setLoading(true);
            }
            else {
                setPageLoading(true);
            }
            setError("");
            const token = localStorage.getItem("token");
            const response = await fetch(`${END_URL}/user-community-post?page=${page}`, {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to load community posts.");
            }
            setPosts(data.posts || []);
            setPagination(data.pagination || null);
            setCurrentPage(data.pagination?.current_page || page);
        }
        catch (err) {
            setError(err.message || "Unable to load community posts.");
        }
        finally {
            setLoading(false);
            setPageLoading(false);
            setLoadingPageNumber(null);
        }
    }, []);

    useEffect(() => {
        fetchPosts(1, true);
    }, [fetchPosts]);

    const handleClose = () => {
        if (submitting) return;

        setShowModal(false);
        setTitle("");
        setDescription("");
        setImage(null);
        setImagePreview("");
        setEditingPostId(null);
        setError("");
    };

    const handleEdit = (post) => {
        setEditingPostId(post.id);
        setTitle(post.community_title || "");
        setDescription(post.community_text || "");
        setImage(null);
        setImagePreview(
            post.community_image
                ? `${IMG_URL}/d0/${post.community_image}`
                : ""
        );
        setError("");
        setShowModal(true);
    };

    const handleDelete = (postId) => {
        setError("");
        setConfirmDeleteId(postId);
    };

    const confirmDelete = async () => {
        if (!confirmDeleteId || deleting) return;

        const token = localStorage.getItem("token");

        if (!token) {
            setError("Please log in to manage community posts.");
            setConfirmDeleteId(null);
            return;
        }

        try {
            setDeleting(true);
            setError("");

            const response = await fetch(
                `${END_URL}/community-posts/${confirmDeleteId}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error(
                        "Your session has expired. Please log in again."
                    );
                }

                throw new Error(
                    data.message || "Failed to delete community post."
                );
            }

            setConfirmDeleteId(null);

            await fetchPosts(currentPage, true);

            setSuccessMessage(
                data.message || "Community post deleted successfully."
            );
            setShowSuccess(true);
        } catch (err) {
            setConfirmDeleteId(null);
            setError(err.message || "Unable to delete community post.");
        } finally {
            setDeleting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const plainDescription = description
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/g, " ")
            .trim();

        if (!title.trim() || !plainDescription) {
            setError("Please enter both title and description.");
            return;
        }

        const token = localStorage.getItem("token");

        if (!token) {
            setError("Please log in to manage community posts.");
            return;
        }

        try {
            setSubmitting(true);
            setError("");

            const formData = new FormData();
            formData.append("community_title", title.trim());
            formData.append("community_text", description.trim());

            if (image) {
                formData.append("community_image", image);
            }

            const isEditing = editingPostId !== null;
            const url = isEditing
                ? `${END_URL}/community-posts/${editingPostId}`
                : `${END_URL}/community-posts`;

            if (isEditing) {
                formData.append("_method", "PUT");
            }

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 422) {
                    const validationErrors = Object.values(data.errors || {}).flat();
                    throw new Error(
                        validationErrors.join(" ") || "Please check the submitted fields."
                    );
                }

                if (response.status === 401) {
                    throw new Error("Your session has expired. Please log in again.");
                }

                throw new Error(
                    data.message ||
                    (isEditing
                        ? "Failed to update community post."
                        : "Failed to create community post.")
                );
            }

            setTitle("");
            setDescription("");
            setImage(null);
            setImagePreview("");
            setEditingPostId(null);
            setShowModal(false);

            await fetchPosts(currentPage, true);

            setSuccessMessage(
                data.message ||
                (isEditing
                    ? "Community post updated successfully."
                    : "Community post created successfully.")
            );

            setShowSuccess(true);
        } catch (err) {
            setError(err.message || "Unable to save community post.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const allowedTypes = ["image/jpeg", "image/png", "image/webp",];
        const maxSize = 5 * 1024 * 1024; // 5 MB
        if (!allowedTypes.includes(file.type)) {
            setError("Only JPEG, PNG, and WEBP images are allowed.");
            e.target.value = "";
            return;
        }
        if (file.size > maxSize) {
            setError("Image size must not exceed 5 MB.");
            e.target.value = "";
            return;
        }
        setError("");
        setImage(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const formatPostDate = (dateString) => {
        if (!dateString) return "";

        const date = new Date(dateString);

        if (Number.isNaN(date.getTime())) return "";

        return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const ConfirmModal = ({ message, onConfirm, onCancel, loading }) => (
        <div className="modal-question history-modal">
            <div className="modal-test">
                <div className="modal-content-test">
                    <div className="modal-dymic">{message}</div>

                    <button
                        type="button"
                        className="modal-close-button-test"
                        onClick={onConfirm}
                        disabled={loading}
                    >
                        {loading ? "Deleting..." : "Yes"}
                    </button>

                    <button
                        type="button"
                        className="modal-close-button-test"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        No
                    </button>
                </div>
            </div>
        </div>
    );

    const SuccessModal = ({ message, onClose }) => (
        <div className="modal-question history-modal">
            <div className="modal-test">
                <div className="modal-content-test">
                    <div className="modal-dymic">{message}</div>

                    <button
                        type="button"
                        className="modal-close-button-test"
                        onClick={onClose}
                    >
                        Ok
                    </button>
                </div>
            </div>
        </div>
    );

    const handlePageChange = (page) => {
        const lastPage = pagination?.last_page || 1;
        if (page === currentPage || page < 1 || page > lastPage || pageLoading) {
            return;
        }
        setLoadingPageNumber(page);
        fetchPosts(page, false);
    };

    const getPaginationPages = () => {
        if (!pagination?.last_page) return [];
        const total = pagination.last_page;
        const current = currentPage;
        if (total <= 7) {
            return Array.from({
                length: total
            }, (_, i) => i + 1);
        }
        if (current <= 4) {
            return [1, 2, 3, 4, 5, "...", total];
        }
        if (current >= total - 3) {
            return [
                1, "...",
                total - 4,
                total - 3,
                total - 2,
                total - 1,
                total,
            ];
        }
        return [
            1, "...",
            current - 2,
            current - 1,
            current,
            current + 1,
            current + 2, "...",
            total,
        ];
    };

    return (
        <div className="result-cont community-posts-cont">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12 col-sm-12 col-md-3 col-lg-3 das-res">
                        <Dashboard globaltest={globaltest} />
                    </div>

                    {/* Community posts content */}
                    <div className="col-12 col-sm-12 col-md-9 col-lg-9 community-posts-main">
                        <div className="community-posts-page">
                            <div className="community-posts-header">
                                <h2>Community Post</h2>

                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setError("");
                                        setShowModal(true);
                                    }}
                                >
                                    Add Post
                                </button>
                            </div>

                            {loading ? (
                                <p className="community-posts-empty">
                                    Loading community posts...
                                </p>
                            ) : posts.length > 0 ? (
                                <div className="community-posts-list">
                                    {posts.map((post) => (
                                        <div className="community-post-card" key={post.id}>
                                            <div className="community-post-card-cover community-post-card-cover-first">
                                                {/* Left: Post content */}
                                                <div className="community-post-content">
                                                    <div className="community-post-content-inner">
                                                        <h4>{post.community_title}</h4>
                                                        <div
                                                            className="community-post-description"
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(
                                                                    (post.community_text || "")
                                                                        .replace(/&nbsp;|&#160;|&#xA0;/gi, " ")
                                                                        .replace(/\u00A0/g, " ")
                                                                ),
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                {post.community_image && (
                                                    <div className="community-post-image-wrapper">
                                                        <img
                                                            src={`${IMG_URL}/d0/${post.community_image}`}
                                                            alt={post.community_title}
                                                            className="community-post-image"
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="community-post-content-bottom">
                                                <div className="community-post-meta">
                                                    {post.user_name && (
                                                        <small>
                                                            Posted by <span>{post.user_name}</span>
                                                        </small>
                                                    )}

                                                    {post.created_at && (
                                                        <small className="community-post-date">
                                                            <span><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"
                                                                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                                                aria-hidden="true">
                                                                <rect x="3" y="5" width="18" height="16" rx="3" />
                                                                <path d="M8 3v4M16 3v4M3 10h18" />
                                                            </svg></span> {formatPostDate(post.created_at)}
                                                        </small>
                                                    )}
                                                </div>

                                                <div className="community-post-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => handleEdit(post)}
                                                    >
                                                        <span><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"
                                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                                            aria-hidden="true">
                                                            <path d="M12 20h9" />
                                                            <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" />
                                                        </svg></span> Edit
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() => handleDelete(post.id)}
                                                    >
                                                        <span><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"
                                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                                            aria-hidden="true">
                                                            <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" />
                                                        </svg></span>
                                                        Delete
                                                    </button>
                                                </div>

                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="community-posts-empty">
                                    No approved community posts available yet.
                                </p>
                            )}

                            {pagination?.last_page > 1 && (
                                <div className="pagination">
                                    <button
                                        type="button"
                                        disabled={currentPage <= 1 || pageLoading}
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    >
                                        {pageLoading && loadingPageNumber === currentPage - 1
                                            ? "Loading..."
                                            : "Previous"}
                                    </button>

                                    {getPaginationPages().map((page, index) => {
                                        if (page === "...") {
                                            return (
                                                <span
                                                    key={`dots-${index}`}
                                                    className="pagination-dots"
                                                >
                                                    ...
                                                </span>
                                            );
                                        }

                                        return (
                                            <button
                                                key={page}
                                                type="button"
                                                disabled={pageLoading}
                                                className={page === currentPage ? "active" : ""}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {pageLoading && loadingPageNumber === page ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" />
                                                        {page}
                                                    </>
                                                ) : (
                                                    page
                                                )}
                                            </button>
                                        );
                                    })}

                                    <button
                                        type="button"
                                        disabled={
                                            currentPage >= (pagination?.last_page || 1) ||
                                            pageLoading
                                        }
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    >
                                        {pageLoading && loadingPageNumber === currentPage + 1
                                            ? "Loading..."
                                            : "Next"}
                                    </button>
                                </div>
                            )}

                            {/* Modal */}
                            {showModal && (
                                <div
                                    className="community-posts-modal-overlay"
                                    onClick={handleClose}
                                >
                                    <div
                                        className="community-posts-modal"
                                        role="dialog"
                                        aria-modal="true"
                                        aria-labelledby="community-post-modal-title"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="community-posts-modal-header">
                                            <h4 id="community-post-modal-title">
                                                {editingPostId !== null ? "Edit Community Post" : "Add Community Post"}
                                            </h4>

                                            <button
                                                type="button"
                                                className="community-posts-close"
                                                onClick={handleClose}
                                                disabled={submitting}
                                                aria-label="Close modal"
                                            >
                                                &times;
                                            </button>
                                        </div>

                                        <form onSubmit={handleSubmit}>
                                            <div className="community-posts-modal-body">
                                                {error && (
                                                    <div className="alert alert-danger" role="alert">
                                                        {error}
                                                    </div>
                                                )}

                                                <div className="form-group mb-3">
                                                    <label htmlFor="post-title" className="form-label">
                                                        Title
                                                    </label>

                                                    <input
                                                        id="post-title"
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Enter post title"
                                                        value={title}
                                                        onChange={(e) => setTitle(e.target.value)}
                                                        maxLength={255}
                                                        required
                                                    />
                                                </div>

                                                <div className="form-group mb-3">
                                                    <label
                                                        htmlFor="post-description"
                                                        className="form-label"
                                                    >
                                                        Description
                                                    </label>

                                                    <div id="post-description">
                                                        <ReactQuill
                                                            theme="snow"
                                                            value={description}
                                                            onChange={setDescription}
                                                            modules={quillModules}
                                                            formats={quillFormats}
                                                            placeholder="Enter post description..."
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group mb-3">
                                                    <label htmlFor="post-image" className="form-label">
                                                        Community Image (optional)
                                                        <small className="text-muted d-block">
                                                            Accepted formats: JPEG, PNG, WEBP. Maximum size: 5 MB.
                                                        </small>
                                                    </label>

                                                    <input
                                                        id="post-image"
                                                        type="file"
                                                        className="form-control"
                                                        accept="image/jpeg,image/png,image/webp"
                                                        onChange={handleImageChange}
                                                    />

                                                    {imagePreview && (
                                                        <img
                                                            src={imagePreview}
                                                            alt="Community post preview"
                                                            style={{
                                                                width: "100%",
                                                                maxWidth: "300px",
                                                                marginTop: "12px",
                                                                borderRadius: "8px",
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="community-posts-modal-footer">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={handleClose}
                                                    disabled={submitting}
                                                >
                                                    Cancel  <span>
                                                        <FontAwesomeIcon className="icc" icon={faArrowRight} />
                                                    </span>
                                                </button>

                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={submitting}
                                                >
                                                    {submitting
                                                        ? "Saving..."
                                                        : editingPostId !== null
                                                            ? "Update Post"
                                                            : "Add Post"}  <span>
                                                        <FontAwesomeIcon className="icc" icon={faArrowRight} />
                                                    </span>
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {confirmDeleteId !== null && (
                                <ConfirmModal
                                    message="Are you sure you want to delete this community post?"
                                    onConfirm={confirmDelete}
                                    onCancel={() => setConfirmDeleteId(null)}
                                    loading={deleting}
                                />
                            )}

                            {showSuccess && (
                                <SuccessModal
                                    message={successMessage}
                                    onClose={() => {
                                        setShowSuccess(false);
                                        setSuccessMessage("");
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CommunityPosts;
